import {
  Container,
  Title,
  Text,
  Group,
  Badge,
  Stack,
  Divider,
  Breadcrumbs,
  Anchor,
  Button,
  Card,
  Image,
  Flex,
  Box,
  Center,
  Grid,
} from '@mantine/core';
import {
  CalendarIcon,
  ClockIcon,
  YoutubeIcon,
  MusicIcon,
  HeadphonesIcon,
  Share2Icon,
  CopyIcon,
  CheckIcon,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { getPostBySlug } from '../../utils/blogUtils';
import { BLOG_DISCLAIMER } from '../../utils/blogConstants';
import BlogPostMeta from '../../components/BlogPostMeta/BlogPostMeta';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './Blog.css';

const BlogPost = () => {
  const { id } = useParams<{ id: string }>();
  const post = id ? getPostBySlug(id) : undefined;
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = async (platform: 'twitter' | 'linkedin' | 'facebook') => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(post?.title || '');
    const text = encodeURIComponent(post?.excerpt || '');

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  // Extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  if (!post) {
    return (
      <Container size='lg' py='xl'>
        <Title order={1}>Post not found</Title>
        <Text mt='md'>
          <Anchor component={Link} to='/resources'>
            Return to resources
          </Anchor>
        </Text>
      </Container>
    );
  }

  const breadcrumbTitle = post.podcastEpisode?.guestName
    ? `Podcast: ${post.podcastEpisode.guestName}`
    : post.title;

  const items = [
    { title: 'Home', href: '/' },
    { title: 'Resources', href: '/resources' },
    { title: breadcrumbTitle, href: '#' },
  ].map((item, index) => (
    <Anchor
      component={Link}
      to={item.href}
      key={index}
      size='sm'
      style={{ wordBreak: 'break-word' }}
    >
      {item.title}
    </Anchor>
  ));

  return (
    <>
      {post && <BlogPostMeta post={post} />}
      <Container size='md' py='xl'>
        <Stack gap='xl'>
          <Breadcrumbs>{items}</Breadcrumbs>

          <div>
            <Group gap='md' mb='md' align='center' wrap='wrap'>
              <Group gap='sm'>
                <Badge color='blue' variant='light' size='lg'>
                  {post.category}
                </Badge>
                {post.podcastEpisode && (
                  <Badge color='purple' variant='light' size='lg'>
                    Episode {post.podcastEpisode.episodeNumber}
                  </Badge>
                )}
              </Group>
              {/* Date and time - desktop only */}
              <Group gap='lg' visibleFrom='sm'>
                <Group gap={4}>
                  <CalendarIcon size={16} />
                  <Text size='sm' c='dimmed'>
                    {new Date(post.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Text>
                </Group>
                {post.readTime && (
                  <Group gap={4}>
                    <ClockIcon size={16} />
                    <Text size='sm' c='dimmed'>
                      {post.readTime}
                    </Text>
                  </Group>
                )}
              </Group>
            </Group>
          </div>

          {/* Podcast Episode Info - Magazine Style */}
          {post.podcastEpisode && (
            <Box>
              {/* Hero Section with Guest Photo */}
              <Stack gap='xl' mb='xl'>
                {/* Title - Desktop: above photo, Mobile: below photo */}
                <Box hiddenFrom='md'>
                  {post.podcastEpisode.guestPhoto && (
                    <Center mb='xl'>
                      <Box
                        style={{
                          width: 250,
                          height: 250,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0,
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                        }}
                      >
                        <Image
                          src={post.podcastEpisode.guestPhoto}
                          alt={post.podcastEpisode.guestName || 'Guest'}
                          width={250}
                          height={250}
                          style={{ objectFit: 'cover' }}
                        />
                      </Box>
                    </Center>
                  )}
                </Box>
                <Center visibleFrom='md'>
                  <Title
                    order={1}
                    style={{
                      fontSize: 'clamp(2rem, 4vw, 3rem)',
                      lineHeight: 1.2,
                      textAlign: 'center',
                    }}
                    dangerouslySetInnerHTML={{ __html: post.title }}
                  />
                </Center>
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  gap='xl'
                  align={{ base: 'center', md: 'center' }}
                >
                  {post.podcastEpisode.guestPhoto && (
                    <Box visibleFrom='md'>
                      <Box
                        style={{
                          width: 250,
                          height: 250,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0,
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                        }}
                      >
                        <Image
                          src={post.podcastEpisode.guestPhoto}
                          alt={post.podcastEpisode.guestName || 'Guest'}
                          width={250}
                          height={250}
                          style={{ objectFit: 'cover' }}
                        />
                      </Box>
                    </Box>
                  )}
                  <Stack gap='md' style={{ flex: 1 }}>
                    <Title
                      order={1}
                      style={{
                        fontSize: 'clamp(2rem, 4vw, 3rem)',
                        lineHeight: 1.2,
                      }}
                      hiddenFrom='md'
                      dangerouslySetInnerHTML={{ __html: post.title }}
                    />
                    {post.podcastEpisode.guestTitle && (
                      <Text size='md' c='dimmed'>
                        {post.podcastEpisode.guestTitle}
                      </Text>
                    )}
                    {post.podcastEpisode.guestBio && (
                      <Text
                        size='md'
                        style={{ lineHeight: 1.7, maxWidth: '600px' }}
                      >
                        {post.podcastEpisode.guestBio}
                      </Text>
                    )}
                    {/* Date and time - mobile only */}
                    <Group gap='lg' mt='sm' hiddenFrom='sm'>
                      <Group gap={4}>
                        <CalendarIcon size={16} />
                        <Text size='sm' c='dimmed'>
                          {new Date(post.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </Text>
                      </Group>
                      {post.readTime && (
                        <Group gap={4}>
                          <ClockIcon size={16} />
                          <Text size='sm' c='dimmed'>
                            {post.readTime}
                          </Text>
                        </Group>
                      )}
                    </Group>
                    {/* Guest Social Links */}
                    {(post.podcastEpisode.guestTwitter ||
                      post.podcastEpisode.guestLinkedIn) && (
                      <Group gap='md' mt='sm' wrap='wrap'>
                        {post.podcastEpisode.guestLinkedIn && (
                          <Anchor
                            href={post.podcastEpisode.guestLinkedIn}
                            target='_blank'
                            rel='noopener noreferrer'
                            size='md'
                            fw={500}
                          >
                            LinkedIn →
                          </Anchor>
                        )}
                        {post.podcastEpisode.guestTwitter && (
                          <Anchor
                            href={post.podcastEpisode.guestTwitter}
                            target='_blank'
                            rel='noopener noreferrer'
                            size='md'
                            fw={500}
                          >
                            Twitter →
                          </Anchor>
                        )}
                      </Group>
                    )}
                  </Stack>
                </Flex>
              </Stack>

              {/* Platform Links and Sharing Section */}
              <Card
                shadow='sm'
                p={{ base: 'md', sm: 'xl' }}
                radius='lg'
                withBorder
              >
                <Flex
                  direction={{ base: 'column', sm: 'row' }}
                  justify='space-between'
                  align={{ base: 'stretch', sm: 'center' }}
                  gap={{ base: 'lg', sm: 'xl' }}
                >
                  <Stack gap='lg' style={{ flex: 1, minWidth: 200 }}>
                    <Stack gap='sm'>
                      <Text fw={600} size='md'>
                        Listen or Watch
                      </Text>
                      <Stack gap='sm' hiddenFrom='sm'>
                        {post.podcastEpisode.youtubeUrl && (
                          <Button
                            component='a'
                            href={post.podcastEpisode.youtubeUrl}
                            target='_blank'
                            leftSection={<YoutubeIcon size={18} />}
                            variant='light'
                            color='red'
                            fullWidth
                          >
                            YouTube
                          </Button>
                        )}
                        {post.podcastEpisode.spotifyUrl && (
                          <Button
                            component='a'
                            href={post.podcastEpisode.spotifyUrl}
                            target='_blank'
                            leftSection={<MusicIcon size={18} />}
                            variant='light'
                            color='green'
                            fullWidth
                          >
                            Spotify
                          </Button>
                        )}
                        {post.podcastEpisode.appleUrl && (
                          <Button
                            component='a'
                            href={post.podcastEpisode.appleUrl}
                            target='_blank'
                            leftSection={<HeadphonesIcon size={18} />}
                            variant='light'
                            color='blue'
                            fullWidth
                          >
                            Apple Podcasts
                          </Button>
                        )}
                      </Stack>
                      <Group gap='sm' wrap='wrap' visibleFrom='sm'>
                        {post.podcastEpisode.youtubeUrl && (
                          <Button
                            component='a'
                            href={post.podcastEpisode.youtubeUrl}
                            target='_blank'
                            leftSection={<YoutubeIcon size={18} />}
                            variant='light'
                            color='red'
                          >
                            YouTube
                          </Button>
                        )}
                        {post.podcastEpisode.spotifyUrl && (
                          <Button
                            component='a'
                            href={post.podcastEpisode.spotifyUrl}
                            target='_blank'
                            leftSection={<MusicIcon size={18} />}
                            variant='light'
                            color='green'
                          >
                            Spotify
                          </Button>
                        )}
                        {post.podcastEpisode.appleUrl && (
                          <Button
                            component='a'
                            href={post.podcastEpisode.appleUrl}
                            target='_blank'
                            leftSection={<HeadphonesIcon size={18} />}
                            variant='light'
                            color='blue'
                          >
                            Apple Podcasts
                          </Button>
                        )}
                      </Group>
                    </Stack>
                    <Stack gap='sm'>
                      <Text fw={600} size='md'>
                        Share This Episode
                      </Text>
                      <Grid gutter='xs' hiddenFrom='sm'>
                        <Grid.Col span={6}>
                          <Button
                            size='xs'
                            variant='light'
                            leftSection={<Share2Icon size={14} />}
                            onClick={() => handleShare('linkedin')}
                            fullWidth
                          >
                            LinkedIn
                          </Button>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Button
                            size='xs'
                            variant='light'
                            leftSection={<Share2Icon size={14} />}
                            onClick={() => handleShare('twitter')}
                            fullWidth
                          >
                            Twitter
                          </Button>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Button
                            size='xs'
                            variant='light'
                            leftSection={<Share2Icon size={14} />}
                            onClick={() => handleShare('facebook')}
                            fullWidth
                          >
                            Facebook
                          </Button>
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <Button
                            size='xs'
                            variant='light'
                            leftSection={
                              copied ? (
                                <CheckIcon size={14} />
                              ) : (
                                <CopyIcon size={14} />
                              )
                            }
                            onClick={handleCopyLink}
                            color={copied ? 'green' : undefined}
                            fullWidth
                          >
                            {copied ? 'Copied!' : 'Copy Link'}
                          </Button>
                        </Grid.Col>
                      </Grid>
                      <Group gap='xs' wrap='wrap' visibleFrom='sm'>
                        <Button
                          size='xs'
                          variant='light'
                          leftSection={<Share2Icon size={14} />}
                          onClick={() => handleShare('linkedin')}
                        >
                          LinkedIn
                        </Button>
                        <Button
                          size='xs'
                          variant='light'
                          leftSection={<Share2Icon size={14} />}
                          onClick={() => handleShare('twitter')}
                        >
                          Twitter
                        </Button>
                        <Button
                          size='xs'
                          variant='light'
                          leftSection={<Share2Icon size={14} />}
                          onClick={() => handleShare('facebook')}
                        >
                          Facebook
                        </Button>
                        <Button
                          size='xs'
                          variant='light'
                          leftSection={
                            copied ? (
                              <CheckIcon size={14} />
                            ) : (
                              <CopyIcon size={14} />
                            )
                          }
                          onClick={handleCopyLink}
                          color={copied ? 'green' : undefined}
                        >
                          {copied ? 'Copied!' : 'Copy Link'}
                        </Button>
                      </Group>
                    </Stack>
                  </Stack>
                  {post.podcastEpisode.youtubeUrl && (
                    <Center hiddenFrom='sm'>
                      <Anchor
                        href={post.podcastEpisode.youtubeUrl}
                        target='_blank'
                        style={{ textDecoration: 'none' }}
                      >
                        <Box
                          style={{
                            width: 140,
                            height: 140,
                            borderRadius: 'var(--mantine-radius-lg)',
                            overflow: 'hidden',
                            flexShrink: 0,
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transition:
                              'transform 0.2s ease, box-shadow 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow =
                              '0 6px 16px rgba(0, 0, 0, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow =
                              '0 4px 12px rgba(0, 0, 0, 0.15)';
                          }}
                        >
                          <Image
                            src='/podcast-show-image.webp'
                            alt='How to Start and Run a Law Firm'
                            width={140}
                            height={140}
                            style={{ objectFit: 'cover' }}
                          />
                        </Box>
                      </Anchor>
                    </Center>
                  )}
                  {post.podcastEpisode.youtubeUrl && (
                    <Box visibleFrom='sm'>
                      <Anchor
                        href={post.podcastEpisode.youtubeUrl}
                        target='_blank'
                        style={{ textDecoration: 'none' }}
                      >
                        <Box
                          style={{
                            width: 140,
                            height: 140,
                            borderRadius: 'var(--mantine-radius-lg)',
                            overflow: 'hidden',
                            flexShrink: 0,
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transition:
                              'transform 0.2s ease, box-shadow 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow =
                              '0 6px 16px rgba(0, 0, 0, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow =
                              '0 4px 12px rgba(0, 0, 0, 0.15)';
                          }}
                        >
                          <Image
                            src='/podcast-show-image.webp'
                            alt='How to Start and Run a Law Firm'
                            width={140}
                            height={140}
                            style={{ objectFit: 'cover' }}
                          />
                        </Box>
                      </Anchor>
                    </Box>
                  )}
                </Flex>
              </Card>
            </Box>
          )}

          <Divider />

          <div
            className='blog-post-content'
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Video Embed (for video posts) - At the bottom */}
          {post.video &&
            post.video.youtubeUrl &&
            (() => {
              const videoId = getYouTubeVideoId(post.video.youtubeUrl);
              if (!videoId) return null;

              return (
                <Center mt='xl'>
                  <Box
                    style={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: '800px',
                      aspectRatio: '16 / 9',
                      borderRadius: 'var(--mantine-radius-md)',
                      overflow: 'hidden',
                      backgroundColor: '#000',
                    }}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0,
                        borderRadius: 'var(--mantine-radius-md)',
                      }}
                      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                      allowFullScreen
                      title={post.title}
                    />
                  </Box>
                </Center>
              );
            })()}

          {/* Guest Website Link (Podcast Episodes Only) - Inline at end of article */}
          {post.podcastEpisode?.guestWebsite && (
            <Text size='md' style={{ lineHeight: 1.7 }}>
              Learn more about {post.podcastEpisode.guestName} at{' '}
              <Anchor
                href={post.podcastEpisode.guestWebsite}
                target='_blank'
                rel='noopener noreferrer'
                fw={500}
              >
                {new URL(post.podcastEpisode.guestWebsite).hostname.replace(
                  'www.',
                  '',
                )}
              </Anchor>
              .
            </Text>
          )}

          {/* Standard Disclaimer Footer (All Posts) */}
          <Box
            pt='md'
            style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
          >
            <Text size='xs' c='dimmed' style={{ fontStyle: 'italic' }}>
              {BLOG_DISCLAIMER}
            </Text>
          </Box>

          {/* Practist Promotional Section (Podcast Episodes Only) */}
          {post.podcastEpisode && (
            <>
              <Divider my='md' />
              <Card shadow='md' padding='xl' radius='md' withBorder>
                <Stack gap='md'>
                  <Group gap='xs' align='center' wrap='nowrap'>
                    <Image
                      src='/main-logo.webp'
                      alt='Practist Logo'
                      h={{ base: 16, sm: 20 }}
                      w='auto'
                      style={{ flexShrink: 0 }}
                    />
                    <Text
                      fw={700}
                      size='lg'
                      variant='gradient'
                      gradient={{
                        from: 'pink.6',
                        to: 'practistBlue.6',
                        deg: 90,
                      }}
                      style={{ lineHeight: 1 }}
                    >
                      This podcast is presented by Practist
                    </Text>
                  </Group>
                  <Text>
                    Practist is law practice management software built for
                    attorneys by people who understand law firms. Everything
                    your law firm needs—intake, billing, documents, and
                    AI—working together from day one.
                  </Text>
                  <Button
                    component='a'
                    href='https://practist.io'
                    target='_blank'
                    size='sm'
                    variant='filled'
                    color='practistBlue'
                    style={{ width: 'fit-content' }}
                    styles={{
                      root: {
                        backgroundColor: 'var(--mantine-color-practistBlue-4)',
                        '&:hover': {
                          backgroundColor:
                            'var(--mantine-color-practistBlue-5)',
                        },
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </Stack>
              </Card>
            </>
          )}
        </Stack>
      </Container>
      <PublicFooter />
    </>
  );
};

export default BlogPost;
